import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import usePublicLocale from "../i18n/usePublicLocale";
import api from "../api";

export default function ErrorOverlay() {
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  usePublicLocale(["overlays"]);
  const { t } = useTranslation("overlays");

  useEffect(() => {
    function onError(e) {
      setError(e?.detail || { status: "unknown", data: null });
    }
    window.addEventListener("http:error", onError);
    return () => window.removeEventListener("http:error", onError);
  }, []);

  if (!error) return null;

  const method = String(error.config?.method || "get").toLowerCase();
  const canRetry =
    Boolean(error.config?.url) && ["get", "head", "options"].includes(method);

  const retryRequest = async () => {
    if (!canRetry || retrying) return;
    setRetrying(true);
    try {
      await api.request({
        ...error.config,
        skipHttpErrorEvent: true,
        skipErrorRedirect: true,
      });
      setError(null);
    } catch (retryError) {
      setError({
        status: retryError?.response?.status || error.status,
        statusText: retryError?.response?.statusText,
        config: error.config,
      });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Snackbar
      open={Boolean(error)}
      onClose={() => setError(null)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        zIndex: 1500,
        p: 2,
      }}
    >
      <Alert
        severity="error"
        variant="filled"
        onClose={() => setError(null)}
        sx={{
          width: 760,
          maxWidth: "100%",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" component="h2">
            {t("errorOverlay.title")}
          </Typography>
          <Typography variant="body2">{t("errorOverlay.message")}</Typography>
          {error.status && error.status !== "unknown" && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
              {error.statusText || `HTTP ${error.status}`}
            </Typography>
          )}
        </Box>
        <Button
          color="inherit"
          size="small"
          onClick={canRetry ? retryRequest : () => window.location.reload()}
          disabled={retrying}
          startIcon={
            retrying ? <CircularProgress color="inherit" size={16} /> : null
          }
          sx={{ flexShrink: 0, mt: 0.5 }}
        >
          {canRetry ? t("errorOverlay.retry") : t("errorOverlay.refresh")}
        </Button>
      </Alert>
    </Snackbar>
  );
}
